// Shared style constants for MealPlan components

export const COLORS = {
    primary: '#007bff',
    success: '#28a745',
    danger: '#dc3545',
    warning: '#ffc107',
    info: '#17a2b8',
    light: '#f8f9fa',
    dark: '#343a40',

    // Status colors
    active: {
        background: '#d4edda',
        border: '#c3e6cb',
        text: '#155724'
    },
    inactive: {
        background: '#f8f9fa',
        border: '#dee2e6',
        text: '#6c757d'
    },
    disabled: {
        background: '#f1f1f1',
        border: '#ccc',
        text: '#999'
    }
};

export const BUTTON_STYLES = {
    substitution: (isActive, isDisabled = false) => ({
        display: 'flex',
        alignItems: 'center',
        backgroundColor: isActive ? COLORS.active.background : (isDisabled ? COLORS.disabled.background : COLORS.inactive.background),
        border: `1px solid ${isActive ? COLORS.active.border : (isDisabled ? COLORS.disabled.border : COLORS.inactive.border)}`,
        borderRadius: '20px',
        padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2.5vw, 12px)',
        fontSize: 'clamp(11px, 2.5vw, 13px)',
        color: isActive ? COLORS.active.text : (isDisabled ? COLORS.disabled.text : COLORS.inactive.text),
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled && !isActive ? 0.5 : 1,
        transition: 'all 0.2s ease'
    }),

    quantity: {
        add: {
            padding: 'clamp(2px, 0.5vw, 3px) clamp(6px, 1.5vw, 8px)',
            border: `1px solid ${COLORS.success}`,
            borderRadius: '3px',
            backgroundColor: 'transparent',
            color: COLORS.success,
            fontSize: 'clamp(10px, 2vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        remove: {
            padding: 'clamp(2px, 0.5vw, 3px) clamp(6px, 1.5vw, 8px)',
            border: `1px solid ${COLORS.danger}`,
            borderRadius: '3px',
            backgroundColor: 'transparent',
            color: COLORS.danger,
            fontSize: 'clamp(10px, 2vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    }
};

export const MODAL_STYLES = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },

    content: {
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        margin: '20px',
        position: 'relative'
    },

    closeButton: {
        position: 'absolute',
        top: '15px',
        right: '15px',
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        zIndex: 1001
    }
};

export const CARD_STYLES = {
    container: (meal) => ({
        position: 'relative',
        border: meal.quantity === 0 ? '2px dashed #ccc' : '4px solid #ffb13c',
        borderRadius: '8px',
        padding: 'clamp(8px, 2vw, 10px)',
        cursor: 'pointer',
        backgroundColor: meal.isTemporary ? '#fff3cd' : meal.quantity !== 0 ? 'inset rgb(246, 245, 237)' : '#f8f9fa',
        transition: 'opacity 0.2s ease',
        minHeight: 'clamp(160px, 25vw, 180px)',
        display: 'flex',
        flexDirection: 'column',
        width: 'clamp(240px, 45vw, 280px)',
        flexShrink: 0,
        opacity: meal.quantity === 0 ? 0.8 : 1
    }),

    image: {
        width: '100%',
        aspectRatio: '1 / 1',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: '4px',
        marginBottom: '8px'
    },

    title: {
        fontSize: 'clamp(12px, 2.5vw, 14px)',
        fontWeight: 'bold',
        flex: '1 1 auto',
        minWidth: '0',
        lineHeight: '1.3'
    }
};

export const SCROLL_ARROW_STYLES = {
    base: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        cursor: 'pointer',
        backgroundColor: COLORS.primary,
        color: 'white',
        fontSize: '18px',
        fontWeight: 'bold',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        zIndex: 10
    },
    left: {
        left: '10px'
    },
    right: {
        right: '10px'
    }
};

export const MESSAGE_STYLES = {
    success: {
        margin: 'clamp(10px, 3vw, 20px)',
        backgroundColor: '#dff0d8',
        border: '1px solid #d6e9c6',
        borderRadius: '5px',
        padding: 'clamp(10px, 2.5vw, 15px)',
        color: '#3c763d',
        fontSize: 'clamp(12px, 2.5vw, 14px)'
    },
    error: {
        margin: 'clamp(10px, 3vw, 20px)',
        backgroundColor: '#f2dede',
        border: '1px solid #ebccd1',
        borderRadius: '5px',
        padding: 'clamp(10px, 2.5vw, 15px)',
        color: '#a94442',
        fontSize: 'clamp(12px, 2.5vw, 14px)'
    },
    warning: {
        marginTop: 'clamp(10px, 2.5vw, 15px)',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        padding: 'clamp(8px, 2vw, 12px)',
        fontSize: 'clamp(11px, 2.5vw, 14px)',
        color: '#856404',
        position: 'relative',
        zIndex: 2
    }
};

export const TOAST_STYLES = {
    container: {
        position: 'fixed',
        // TOP CENTER (current: top-right)
        // top: '20px',
        // left: '50%',
        // transform: 'translateX(-50%)',

        // Alternative positions - uncomment one and comment others:
        // TOP RIGHT
        // top: '20px',
        // right: '20px',

        // TOP LEFT
        // top: '20px',
        // left: '20px',

        //BOTTOM CENTER
        bottom: 'clamp(10px, 3vw, 20px)',
        left: '50%',
        transform: 'translateX(-50%)',

        // BOTTOM RIGHT
        // bottom: '20px',
        // right: '20px',

        // BOTTOM LEFT
        // bottom: '20px',
        // left: '20px',

        padding: 'clamp(12px, 2.5vw, 16px) clamp(16px, 3vw, 20px)',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 9999,
        minWidth: 'clamp(250px, 60vw, 300px)',
        maxWidth: 'clamp(400px, 90vw, 500px)',
        transition: 'all 0.3s ease',
        pointerEvents: 'none'
    },
    success: {
        backgroundColor: '#d4edda',
        border: '1px solid #c3e6cb',
        color: '#155724'
    },
    error: {
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        color: '#721c24'
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(8px, 2vw, 12px)'
    },
    icon: {
        fontSize: 'clamp(14px, 3vw, 18px)',
        flexShrink: 0
    },
    message: {
        fontSize: 'clamp(12px, 2.5vw, 14px)',
        fontWeight: '500',
        lineHeight: '1.4'
    }
};